import * as React from 'react';
import { ScopeButton } from '@monorepo/shared-lib';
import { Space } from 'antd';
import { ReactComponent as HmrcIcon } from 'assets/images/icons/hmrcIcon.svg';
import { StyledTooltip } from 'components/Custom/Tooltip';
import i18n from 'i18next';
import { AccountType } from 'store/api/enums';
import {
  AdvancedUserRightsCodes,
  DataLogActions,
  DataLogStatus,
} from 'store/app/enums';
import { User } from 'store/auth/models';
import { Entity } from 'store/organisation/models';
import { VatRunData } from 'store/vat/models';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { getDataLogActionLabel } from 'utils/display-enum-label';

const t = i18n.t.bind(i18n);

export const getNextButton = (
  handleMenuClick: (key: DataLogActions) => void,
  vatRunData: VatRunData,
  currentUser: User,
  checkClient: () => boolean,
  currentOrganisation?: Entity,
  hasAdvancedUserRights: boolean = false,
  rightsTooltipText?: string,
  approveRightsTooltipText?: string
) => {
  const handleClick = (action: DataLogActions) => {
    handleMenuClick(action);
  };
  const setNotApproved = () => {
    handleMenuClick(DataLogActions.SET_NOT_APPROVED);
  };
  // const showNoApprove =
  //   vatRunData.status &&
  //   ((DataLogStatus[vatRunData.status] === DataLogStatus.AWAITING_APPROVAL &&
  //     (currentUser.accountUuid === vatRunData.reviewerId ||
  //       currentUser.accountUuid === vatRunData.preparerId ||
  //       AccountType[currentUser.accountType] === AccountType.OWNER)) ||
  //     (DataLogStatus[vatRunData.status] ===
  //       DataLogStatus.AWAITING_CLIENT_APPROVAL &&
  //       currentUser.accountUuid === vatRunData.clientId));

  const showNoApprove =
    vatRunData.status &&
    DataLogStatus[vatRunData.status] ===
      DataLogStatus.AWAITING_CLIENT_APPROVAL &&
    currentUser.accountUuid === vatRunData.clientId;

  let selecetedAction = DataLogActions.SAVE_VAT_RETURN;
  let isDisabled = false;
  if (vatRunData.status) {
    switch (DataLogStatus[vatRunData.status]) {
      case DataLogStatus.SAVED:
        selecetedAction = DataLogActions.SET_AWAITING_APPROVAL;
        break;
      case DataLogStatus.AWAITING_APPROVAL:
        if (
          vatRunData.status &&
          DataLogStatus[vatRunData.status] ===
            DataLogStatus.AWAITING_APPROVAL &&
          (currentUser.accountUuid === vatRunData.reviewerId ||
            currentUser.accountUuid === vatRunData.preparerId ||
            AccountType[currentUser.accountType] === AccountType.OWNER)
        ) {
          selecetedAction = DataLogActions.SET_INTERNAL_APPROVED;
        } else if (!!vatRunData.preparerId) {
          isDisabled = true;
          selecetedAction = DataLogActions.SET_INTERNAL_APPROVED;
        }
        break;
      case DataLogStatus.INTERNAL_APPROVED:
        selecetedAction = DataLogActions.SEND_FOR_CLIENT_APPROVAL;
        break;
      case DataLogStatus.AWAITING_CLIENT_APPROVAL:
        if (currentUser.accountUuid === vatRunData.clientId) {
          selecetedAction = DataLogActions.SET_CLIENT_APPROVED;
        } else selecetedAction = DataLogActions.LOCK_AS_APPROVED;
        break;
      case DataLogStatus.CLIENT_APPROVED:
      case DataLogStatus.LOCKED:
        selecetedAction = DataLogActions.MARK_AS_SUBMITTED;
    }
  }

  const isStaff = AccountType[currentUser.accountType] === AccountType.STAFF;
  const lacksCreateSubmissionRight =
    isStaff &&
    hasAdvancedUserRights &&
    !!currentOrganisation &&
    !hasAdvancedUserRight(
      currentUser,
      currentOrganisation,
      AdvancedUserRightsCodes.CREATE_SUBMISSION
    );

  // Allow overriding disablement when the user is allowed to approve and the selected action is SET_CLIENT_APPROVED
  const hasApproveSubmissionRight =
    !!currentOrganisation &&
    (!hasAdvancedUserRights ||
      hasAdvancedUserRight(
        currentUser,
        currentOrganisation,
        AdvancedUserRightsCodes.APPROVE_SUBMISSION
      ));
  const isApproveAction =
    selecetedAction === DataLogActions.SET_INTERNAL_APPROVED;
  const lacksApproveSubmissionRight =
    isApproveAction && !hasApproveSubmissionRight;
  const shouldDisableButton =
    isDisabled ||
    (isApproveAction ? !hasApproveSubmissionRight : lacksCreateSubmissionRight);
  const tooltipText = (() => {
    if (!shouldDisableButton) return '';
    // If both are missing or create missing, prefer create message
    if (lacksCreateSubmissionRight) return rightsTooltipText || '';
    // If create allowed but approve missing
    if (lacksApproveSubmissionRight)
      return approveRightsTooltipText || rightsTooltipText || '';
    return rightsTooltipText || '';
  })();

  // Orange when user needs to save (Draft / Not Saved); green for rest of workflow
  const needsSave = selecetedAction === DataLogActions.SAVE_VAT_RETURN;
  const buttonType = needsSave ? 'dark-orange' : 'primary';
  const country = (vatRunData.taxReportCountry ?? '')
    .toString()
    .trim()
    .toLowerCase();
  const isUkReturn =
    country === 'uk' ||
    country === 'united kingdom' ||
    (vatRunData.taxReportTypeName ?? '').toLowerCase().includes('uk');

  return (
    <Space>
      <>
        {!checkClient() && (
          <StyledTooltip // @ts-ignore
            title={tooltipText}
            placement="top"
            arrow={false}
          >
            <ScopeButton
              onClick={() => handleClick(selecetedAction)}
              type={buttonType} //@ts-ignore
              width={showNoApprove ? '160px' : '240px'}
              disabled={shouldDisableButton}
              icon={
                selecetedAction === DataLogActions.MARK_AS_SUBMITTED &&
                isUkReturn ? (
                  <HmrcIcon style={{ width: 20, height: 20 }} />
                ) : undefined
              }
            >
              {selecetedAction === DataLogActions.SET_INTERNAL_APPROVED ||
              selecetedAction === DataLogActions.SET_CLIENT_APPROVED
                ? t('vat-report.approve')
                : selecetedAction === DataLogActions.MARK_AS_SUBMITTED
                ? isUkReturn
                  ? t('vat-report.submit-to-hmrc')
                  : t('vat-report.submitted-with-authorities')
                : getDataLogActionLabel(selecetedAction, t)}
            </ScopeButton>
          </StyledTooltip>
        )}
        {showNoApprove && (
          <ScopeButton
            onClick={() => setNotApproved()}
            type="default" //@ts-ignore
            width="160px"
          >
            {i18n.t('vat-report.reject')}
          </ScopeButton>
        )}
      </>
    </Space>
  );
};
